import { Chapter, ChapterStatus, ChapterTest, Lesson, Module, NewChapter } from 'types/admin/modules';
import { create } from 'zustand';

export type ChaptersData = Pick<Module, 'chapters'>;
type State = ChaptersData & { initializing: boolean; loading: boolean };

type Actions = {
  setInitialChapters: (chapters: Chapter[]) => void;
  addChapter: () => void;
  deleteChapter: (index: number) => void;
  updateChapterTitle: (index: number, title: string) => void;
  updateChapterStatus: (index: number, status: ChapterStatus) => void;
  addLesson: (index: number, newLessons: Lesson[]) => void;
  deleteLesson: (chapterIndex: number, lessonIndex: number) => void;
  addChapterTest: (index: number, chapterTest: ChapterTest) => void;
  updateLesson: (chapterIndex: number, lessonIndex: number, updatedLesson: Lesson) => void;
  updateChapterTest: (index: number, chapterTest: ChapterTest) => void;
  sortChapters: (from: number, to: number) => void;
  sortLessons: (chapterIndex: number, from: number, to: number) => void;
  reset: () => void;
  setLoadingInitialData: (initializing: boolean) => void;
  setLoading: (loading: boolean) => void;
};

export const useChaptersStore = create<State & Actions>((set, get, store) => ({
  chapters: [],
  initializing: false,
  loading: false,
  setInitialChapters: (chapters: Chapter[]) => set((state: State) => ({ chapters })),
  addChapter: () => set((state: State) => ({ chapters: [...state.chapters, emptyChapter] })),
  deleteChapter: (index: number) => set((state: State) => ({ chapters: state.chapters.filter((chapter, i) => i !== index) })),
  updateChapterTitle: (index: number, title: string) =>
    set((state: State) => ({ chapters: state.chapters.map((chapter, i) => (i !== index ? chapter : { ...chapter, title })) })),
  updateChapterStatus: (index: number, status: ChapterStatus) =>
    set((state: State) => ({ chapters: state.chapters.map((chapter, i) => (i !== index ? chapter : { ...chapter, status })) })),
  addLesson: (index: number, newLessons: Lesson[]) =>
    set((state: State) => ({
      chapters: state.chapters.map((chapter, i) => (i !== index ? chapter : { ...chapter, lessons: [...chapter.lessons, ...newLessons] })),
    })),
  deleteLesson: (chapterIndex: number, lessonIndex: number) =>
    set((state: State) => ({
      chapters: state.chapters.map((chapter, i) =>
        i !== chapterIndex ? chapter : { ...chapter, lessons: chapter.lessons.filter((lesson, index) => lessonIndex !== index) }
      ),
    })),
  addChapterTest: (index: number, chapterTest: ChapterTest) =>
    set((state: State) => ({ chapters: state.chapters.map((chapter, i) => (i !== index ? chapter : { ...chapter, chapterTest })) })),
  updateLesson: (chapterIndex: number, lessonIndex: number, updatedLesson: Lesson) =>
    set((state: State) => ({
      chapters: state.chapters.map((chapter, i) =>
        i !== chapterIndex
          ? chapter
          : { ...chapter, lessons: chapter.lessons.map((lesson, i) => (i !== lessonIndex ? lesson : updatedLesson)) }
      ),
    })),
  updateChapterTest: (index: number, chapterTest: ChapterTest) =>
    set((state: State) => ({ chapters: state.chapters.map((chapter, i) => (i !== index ? chapter : { ...chapter, chapterTest })) })),
  sortChapters: (from: number, to: number) => set((state: State) => ({ chapters: moveItems(state.chapters, from, to) as Chapter[] })),
  sortLessons: (chapterIndex: number, from: number, to: number) =>
    set((state: State) => ({
      chapters: state.chapters.map((chapter, index) =>
        index !== chapterIndex ? chapter : { ...chapter, lessons: moveItems(chapter.lessons, from, to) as Lesson[] }
      ),
    })),
  reset: () => set(store.getInitialState),
  setLoadingInitialData: (initializing: boolean) => set(() => ({ initializing })),
  setLoading: (loading: boolean) => set(() => ({ loading })),
}));

const emptyChapter: NewChapter = { title: '', status: 'Hidden', lessons: [] };

const moveItems = (array: Chapter[] | Lesson[], from: number, to: number) => {
  const newArr = [...array];
  const item = newArr.splice(from, 1)[0];
  newArr.splice(to, 0, item);

  return newArr;
};
